import React, {useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {
  GET_ORGANIZATION_BRAND_PROFILE_IMPACT,
  GET_ORGANIZATION_BRAND_PROFILES,
} from '../graphql/queries';
import {
  CREATE_ORGANIZATION_BRAND_PROFILE,
  DISCOVER_BRAND_FROM_URL,
  PROMOTE_ORGANIZATION_BRAND_PROFILE,
  SET_ORGANIZATION_DEFAULT_BRAND_PROFILE,
  UPDATE_ORGANIZATION_BRAND_PROFILE,
} from '../graphql/mutations';
import BrandingReviewCard, {BrandingSelection} from '../components/branding/BrandingReviewCard';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

interface BrandProfileImpactPayload {
  profileId: string;
  orgId: string;
  orgDefaultGuideCount: number;
  pinnedImportProfileGuideCount: number;
  migratableGuideIds: string[];
}

const DEFAULT_SELECTION: BrandingSelection = {
  organizationDisplayName: '',
  logoUrl: '',
  headerLogoUrl: '',
  primaryColor: '#2563eb',
  secondaryColor: '#dbeafe',
  tone: 'NEUTRAL',
};

const APPROVAL_CONFIDENCE_THRESHOLD = 0.65;

export default function OrganizationBrandingPage() {
  useDocumentTitle('Organization Branding');
  const {activeOrg} = useOrg();

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileStatus, setNewProfileStatus] = useState('DRAFT');
  const [newProfileDefault, setNewProfileDefault] = useState(false);
  const [newSelection, setNewSelection] = useState<BrandingSelection>(DEFAULT_SELECTION);

  const [importUrl, setImportUrl] = useState('');
  const [importProfileName, setImportProfileName] = useState('');
  const [importSetDefault, setImportSetDefault] = useState(false);
  const [importReviewedApproval, setImportReviewedApproval] = useState(false);
  const [importSelection, setImportSelection] = useState<BrandingSelection>(DEFAULT_SELECTION);
  const [discoveryResult, setDiscoveryResult] = useState<any>(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [selectedPromoteProfile, setSelectedPromoteProfile] = useState<any | null>(null);
  const [migratePinnedGuides, setMigratePinnedGuides] = useState(false);

  const {data, loading, refetch} = useQuery(GET_ORGANIZATION_BRAND_PROFILES, {
    variables: {orgId: activeOrg?.id, includeArchived: true},
    skip: !activeOrg?.id,
    fetchPolicy: 'network-only',
  });

  const [discoverBrandFromUrl, {loading: discovering}] = useMutation(DISCOVER_BRAND_FROM_URL);
  const [createProfile, {loading: creatingProfile}] = useMutation(CREATE_ORGANIZATION_BRAND_PROFILE);
  const [updateProfile] = useMutation(UPDATE_ORGANIZATION_BRAND_PROFILE);
  const [setDefaultProfile] = useMutation(SET_ORGANIZATION_DEFAULT_BRAND_PROFILE);
  const [loadProfileImpact, {data: impactData, loading: impactLoading}] = useLazyQuery(
    GET_ORGANIZATION_BRAND_PROFILE_IMPACT,
    {fetchPolicy: 'network-only'}
  );
  const [promoteProfile, {loading: promotingProfile}] = useMutation(PROMOTE_ORGANIZATION_BRAND_PROFILE);

  const profiles = useMemo(() => data?.organizationBrandProfiles || [], [data]);
  const suggestedDefaultId = useMemo(() => {
    const activeProfiles = profiles.filter((profile: any) => profile.status !== 'ARCHIVED');
    if (activeProfiles.length === 0) return null;
    return [...activeProfiles]
      .sort((a: any, b: any) => (b.confidenceScore || 0) - (a.confidenceScore || 0))[0]
      .id;
  }, [profiles]);
  const selectedProfileImpact = useMemo(
    () => impactData?.organizationBrandProfileImpact as BrandProfileImpactPayload | undefined,
    [impactData]
  );

  const showMessage = (
    message: string,
    severity: 'success' | 'error' | 'info'
  ) => {
    setSnackbar({open: true, message, severity});
  };

  const buildCreateInput = (
    name: string,
    selection: BrandingSelection,
    status: string,
    setAsDefault: boolean,
    confidenceScore?: number,
    createdFromImportId?: string,
    provenance?: string[]
  ) => ({
    orgId: activeOrg?.id,
    name,
    status,
    setAsDefault,
    organizationDisplayName: selection.organizationDisplayName || null,
    logoUrl: selection.logoUrl || null,
    headerLogoUrl: selection.headerLogoUrl || null,
    palette: {
      primary: selection.primaryColor || '#2563eb',
      secondary: selection.secondaryColor || '#dbeafe',
      background: '#f9fafb',
      surface: '#ffffff',
      text: '#111827',
    },
    tone: selection.tone || 'NEUTRAL',
    ...(confidenceScore !== undefined ? {confidenceScore} : {}),
    ...(createdFromImportId ? {createdFromImportId} : {}),
    provenance: provenance && provenance.length > 0
      ? provenance
      : createdFromImportId
        ? [`source:${createdFromImportId}`]
        : ['manual-entry'],
  });

  const handleCreateProfile = async () => {
    const profileName = newProfileName.trim();
    if (!profileName) {
      showMessage('Profile name is required', 'error');
      return;
    }
    try {
      await createProfile({
        variables: {
          input: buildCreateInput(
            profileName,
            newSelection,
            newProfileStatus,
            newProfileDefault
          ),
        },
      });
      await refetch();
      setCreateDialogOpen(false);
      setNewProfileName('');
      setNewSelection(DEFAULT_SELECTION);
      setNewProfileStatus('DRAFT');
      setNewProfileDefault(false);
      showMessage('Brand profile created', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Failed to create profile', 'error');
    }
  };

  const handleDiscoverBrand = async () => {
    const trimmed = importUrl.trim();
    if (!trimmed) {
      showMessage('Please enter a source URL', 'error');
      return;
    }
    try {
      const result = await discoverBrandFromUrl({
        variables: {
          input: {
            orgId: activeOrg?.id,
            sourceUrl: trimmed,
          },
        },
      });
      const discovery = result.data?.discoverBrandFromUrl;
      setDiscoveryResult(discovery);
      setImportSelection({
        organizationDisplayName: discovery?.selectedCandidates?.organizationDisplayName || activeOrg?.name || '',
        logoUrl: discovery?.selectedCandidates?.logoUrl || '',
        headerLogoUrl: discovery?.selectedCandidates?.headerLogoUrl || '',
        primaryColor: discovery?.selectedCandidates?.primaryColor || '#2563eb',
        secondaryColor: discovery?.selectedCandidates?.secondaryColor || '#dbeafe',
        tone: discovery?.selectedCandidates?.tone || 'NEUTRAL',
      });
      setImportProfileName(
        `${discovery?.selectedCandidates?.organizationDisplayName || activeOrg?.name || 'Imported'} profile`
      );
      setImportReviewedApproval((discovery?.confidence || 0) >= APPROVAL_CONFIDENCE_THRESHOLD);
      showMessage('Brand discovery complete', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Brand discovery failed', 'error');
    }
  };

  const handleSaveImportedProfile = async () => {
    const profileName = importProfileName.trim();
    if (!profileName || !discoveryResult) {
      showMessage('Profile name and discovery result are required', 'error');
      return;
    }
    const approvedForUsage = importReviewedApproval;
    const status = approvedForUsage ? 'APPROVED' : 'DRAFT';
    const setAsDefault = approvedForUsage ? importSetDefault : false;
    const provenance = [
      `source:${importUrl.trim()}`,
      approvedForUsage ? 'reviewed:manual' : 'reviewed:pending',
    ];
    try {
      await createProfile({
        variables: {
          input: buildCreateInput(
            profileName,
            importSelection,
            status,
            setAsDefault,
            discoveryResult.confidence,
            importUrl.trim(),
            provenance
          ),
        },
      });
      await refetch();
      closeImportDialog();
      showMessage(
        approvedForUsage
          ? 'Imported brand saved as approved profile'
          : 'Imported brand saved as draft profile. Review and approve before applying.',
        approvedForUsage ? 'success' : 'info'
      );
    } catch (error: any) {
      showMessage(error.message || 'Failed to save imported profile', 'error');
    }
  };

  const handleSetDefault = async (profileId: string) => {
    try {
      await setDefaultProfile({variables: {profileId}});
      await refetch();
      showMessage('Default brand profile updated', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Failed to set default profile', 'error');
    }
  };

  const handleStatusUpdate = async (profileId: string, status: string) => {
    try {
      await updateProfile({
        variables: {
          input: {
            profileId,
            status,
          },
        },
      });
      await refetch();
      showMessage('Profile status updated', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Failed to update status', 'error');
    }
  };

  const openPromoteDialog = async (profile: any) => {
    setSelectedPromoteProfile(profile);
    setMigratePinnedGuides(false);
    setPromoteDialogOpen(true);
    try {
      await loadProfileImpact({variables: {profileId: profile.id}});
    } catch (error: any) {
      showMessage(error.message || 'Failed to load profile impact', 'error');
    }
  };

  const handlePromoteProfile = async () => {
    if (!selectedPromoteProfile?.id) return;
    try {
      await promoteProfile({
        variables: {
          input: {
            profileId: selectedPromoteProfile.id,
            setAsDefault: true,
            migratePinnedImportProfileGuides: migratePinnedGuides,
          },
        },
      });
      await refetch();
      setPromoteDialogOpen(false);
      setSelectedPromoteProfile(null);
      showMessage(
        migratePinnedGuides
          ? 'Profile promoted and pinned guides migrated'
          : 'Profile promoted as organization default',
        'success'
      );
    } catch (error: any) {
      showMessage(error.message || 'Failed to promote profile', 'error');
    }
  };

  const closeImportDialog = () => {
    setImportDialogOpen(false);
    setImportUrl('');
    setImportProfileName('');
    setImportSetDefault(false);
    setImportReviewedApproval(false);
    setImportSelection(DEFAULT_SELECTION);
    setDiscoveryResult(null);
  };

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3}}>
        <Box>
          <Typography sx={{fontSize: 24, fontWeight: 650}}>
            Organization Branding
          </Typography>
          <Typography sx={{fontSize: 14, color: 'text.secondary', mt: 0.5}}>
            Manage brand profiles and import branding from a URL.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined"
            onClick={() => {
              setImportDialogOpen(true);
              setImportUrl('');
              setImportProfileName('');
              setImportSetDefault(false);
              setImportReviewedApproval(false);
              setImportSelection(DEFAULT_SELECTION);
              setDiscoveryResult(null);
            }}
          >
            Import brand
          </Button>
          <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
            Create profile
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
          <CircularProgress />
        </Box>
      ) : profiles.length === 0 ? (
        <Alert severity="info">
          No brand profiles yet. Create one manually or use “Import brand” to bootstrap from a website.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {profiles.map((profile: any) => (
            <BrandingReviewCard
              key={profile.id}
              title={profile.name}
              subtitle={`Status: ${profile.status}`}
              confidence={profile.confidenceScore}
              selection={{
                organizationDisplayName: profile.organizationDisplayName || activeOrg?.name || '',
                logoUrl: profile.logoUrl,
                headerLogoUrl: profile.headerLogoUrl,
                primaryColor: profile.palette?.primary,
                secondaryColor: profile.palette?.secondary,
                tone: profile.tone,
              }}
              actions={(
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {profile.isDefault && <Chip color="success" size="small" label="Default" />}
                  {!profile.isDefault && suggestedDefaultId === profile.id && (
                    <Chip color="warning" size="small" variant="outlined" label="Suggested default" />
                  )}
                  {!profile.isDefault && profile.status !== 'ARCHIVED' && (
                    <Button size="small" onClick={() => handleSetDefault(profile.id)}>
                      Set as default
                    </Button>
                  )}
                  {profile.status !== 'ARCHIVED' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openPromoteDialog(profile)}
                    >
                      Promote
                    </Button>
                  )}
                  {profile.status !== 'APPROVED' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleStatusUpdate(profile.id, 'APPROVED')}
                    >
                      Approve
                    </Button>
                  )}
                  {profile.status !== 'ARCHIVED' ? (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleStatusUpdate(profile.id, 'ARCHIVED')}
                    >
                      Archive
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleStatusUpdate(profile.id, 'DRAFT')}
                    >
                      Restore draft
                    </Button>
                  )}
                </Stack>
              )}
            />
          ))}
        </Stack>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create brand profile</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{mt: 1}}>
            <TextField
              label="Profile name"
              value={newProfileName}
              onChange={event => setNewProfileName(event.target.value)}
            />
            <TextField
              select
              label="Status"
              value={newProfileStatus}
              onChange={event => setNewProfileStatus(event.target.value)}
            >
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </TextField>
            <FormControlLabel
              control={(
                <Switch
                  checked={newProfileDefault}
                  onChange={event => setNewProfileDefault(event.target.checked)}
                />
              )}
              label="Set as organization default"
            />
            <BrandingReviewCard
              title="Profile details"
              editable
              selection={newSelection}
              onChange={setNewSelection}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateProfile}
            disabled={creatingProfile}
          >
            {creatingProfile ? <CircularProgress size={16} sx={{mr: 1}} /> : null}
            Save profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={closeImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import brand from URL</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{mt: 1}}>
            <TextField
              label="Source URL"
              value={importUrl}
              onChange={event => setImportUrl(event.target.value)}
              placeholder="https://example.edu"
            />
            <Button
              variant="outlined"
              onClick={handleDiscoverBrand}
              disabled={discovering}
              sx={{alignSelf: 'flex-start'}}
            >
              {discovering ? <CircularProgress size={16} sx={{mr: 1}} /> : null}
              Discover brand
            </Button>
            {discoveryResult && (
              <>
                <TextField
                  label="Profile name"
                  value={importProfileName}
                  onChange={event => setImportProfileName(event.target.value)}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={importSetDefault}
                      onChange={event => setImportSetDefault(event.target.checked)}
                      disabled={!importReviewedApproval}
                    />
                  )}
                  label="Set as organization default"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={importReviewedApproval}
                      onChange={event => setImportReviewedApproval(event.target.checked)}
                    />
                  )}
                  label="I reviewed this import and approve it for guide usage"
                />
                <BrandingReviewCard
                  title="Imported brand review"
                  subtitle="Edit values before saving the profile."
                  confidence={discoveryResult.confidence}
                  warnings={discoveryResult.warnings || []}
                  selection={importSelection}
                  editable
                  onChange={setImportSelection}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveImportedProfile}
            disabled={!discoveryResult || creatingProfile}
          >
            Save as profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promoteDialogOpen}
        onClose={() => {
          setPromoteDialogOpen(false);
          setSelectedPromoteProfile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Promote brand profile</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{mt: 1}}>
            <Typography sx={{fontSize: 14, color: 'text.secondary'}}>
              {selectedPromoteProfile?.name
                ? `Promote "${selectedPromoteProfile.name}" as the organization default profile.`
                : 'Promote this profile as the organization default profile.'}
            </Typography>
            {impactLoading ? (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <CircularProgress size={16} />
                <Typography sx={{fontSize: 13, color: 'text.secondary'}}>
                  Loading guide impact...
                </Typography>
              </Box>
            ) : selectedProfileImpact ? (
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 1.5,
                  bgcolor: '#F9FAFB',
                  p: 1.5,
                }}
              >
                <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', mb: 0.75}}>
                  Impact summary
                </Typography>
                <Typography sx={{fontSize: 13, color: '#374151'}}>
                  ORG_DEFAULT guides inheriting this change: {selectedProfileImpact.orgDefaultGuideCount}
                </Typography>
                <Typography sx={{fontSize: 13, color: '#374151'}}>
                  Pinned IMPORT_PROFILE guides that can be migrated: {selectedProfileImpact.pinnedImportProfileGuideCount}
                </Typography>
              </Box>
            ) : null}
            <FormControlLabel
              control={(
                <Switch
                  checked={migratePinnedGuides}
                  onChange={event => setMigratePinnedGuides(event.target.checked)}
                  disabled={!selectedProfileImpact || selectedProfileImpact.pinnedImportProfileGuideCount === 0}
                />
              )}
              label="Migrate pinned IMPORT_PROFILE guides to this profile"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPromoteDialogOpen(false);
              setSelectedPromoteProfile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePromoteProfile}
            disabled={!selectedPromoteProfile || promotingProfile}
          >
            {promotingProfile ? <CircularProgress size={16} sx={{mr: 1}} /> : null}
            Promote profile
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({...prev, open: false}))}
          sx={{width: '100%'}}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
